import { z } from 'zod';
import {
  lectureSaveBodySchema,
  lectureSnapshotSchema,
  userErrorMessage,
  type LectureRecord,
  type LectureRecordSummary,
  type LectureSnapshot,
} from '@wx-weiqi/contracts';
import type { LectureRecordApi } from '../services/lecture-records';
import { secureRandomUuid } from '../services/random-id';
import { nextRequestId } from '../services/http';
import { lectureContent, lectureText } from '../domain/lecture-records';
const draftSchema = z.object({
  id: z.uuid(),
  version: z.number().int().nonnegative(),
  title: z.string().max(120),
  text: z.string().max(12_000),
  snapshot: lectureSnapshotSchema,
  revision: z.number().int().nonnegative(),
  savedRevision: z.number().int(),
  operation: z
    .object({ requestId: z.string(), revision: z.number().int(), body: lectureSaveBodySchema })
    .nullable(),
});
export type LectureDraft = z.infer<typeof draftSchema>;
interface State {
  drafts: LectureDraft[];
  activeId: string | null;
  records: LectureRecordSummary[];
  nextCursor: string | null;
  busy: boolean;
  message: string;
  error: string;
  storageError: string;
}
export class LectureRecordStore {
  private state: State = {
    drafts: [],
    activeId: null,
    records: [],
    nextCursor: null,
    busy: false,
    message: '',
    error: '',
    storageError: '',
  };
  private listeners = new Set<() => void>();
  private detached = false;
  attach() {
    this.detached = false;
    return () => {
      this.detached = true;
    };
  }
  private readonly key: string;
  constructor(
    readonly api: LectureRecordApi,
    scope: string,
    private readonly storage: Pick<Storage, 'getItem' | 'setItem'> | null,
  ) {
    this.key = `wx-lecture-records-v1:${scope}`;
    try {
      const raw = storage?.getItem(this.key);
      if (raw) {
        const drafts = z.array(draftSchema).max(20).parse(JSON.parse(raw));
        this.state = { ...this.state, drafts, activeId: drafts[0]?.id ?? null };
      }
    } catch {
      this.state.storageError = '本机草稿无法读取，请勿清理浏览器数据。';
    }
  }
  snapshot = () => this.state;
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  private publish(patch: Partial<State>, persist = false) {
    if (this.detached) return;
    this.state = { ...this.state, ...patch };
    if (persist) {
      try {
        if (!this.storage) throw new Error();
        this.storage.setItem(this.key, JSON.stringify(this.state.drafts));
        this.state.storageError = '';
      } catch {
        this.state.storageError = '本机草稿未能备份，请保持页面打开并保存到服务器。';
      }
    }
    this.listeners.forEach((l) => l());
  }
  active() {
    return this.state.drafts.find((d) => d.id === this.state.activeId) ?? null;
  }
  start(snapshot: LectureSnapshot) {
    if (this.state.drafts.length >= 20) {
      this.publish({ error: '本机已有20份草稿，请先在记录列表中移除不需要的草稿。' });
      return;
    }
    const d: LectureDraft = {
      id: secureRandomUuid(),
      version: 0,
      title: '',
      text: '',
      snapshot: lectureSnapshotSchema.parse(snapshot),
      revision: 0,
      savedRevision: -1,
      operation: null,
    };
    this.publish(
      { drafts: [d, ...this.state.drafts], activeId: d.id, error: '', message: '' },
      true,
    );
  }
  select(id: string) {
    if (this.state.drafts.some((d) => d.id === id))
      this.publish({ activeId: id, error: '', message: '' });
  }
  edit(patch: Pick<Partial<LectureDraft>, 'title' | 'text'>) {
    const d = this.active();
    if (!d) return;
    this.publish(
      {
        drafts: this.state.drafts.map((r) =>
          r.id === d.id ? { ...r, ...patch, revision: r.revision + 1 } : r,
        ),
        message: '',
      },
      true,
    );
  }
  discard(id: string) {
    if (this.state.busy) return;
    const drafts = this.state.drafts.filter((d) => d.id !== id);
    this.publish(
      {
        drafts,
        activeId: this.state.activeId === id ? (drafts[0]?.id ?? null) : this.state.activeId,
        error: '',
        message: '',
      },
      true,
    );
  }
  async loadMore(reset = false) {
    if (this.state.busy) return;
    this.publish({ busy: true, error: '' });
    try {
      const page = await this.api.list(reset ? null : this.state.nextCursor);
      this.publish({
        records: reset
          ? page.records
          : [
              ...this.state.records,
              ...page.records.filter(
                (r) => !this.state.records.some((p) => p.recordId === r.recordId),
              ),
            ],
        nextCursor: page.nextCursor,
      });
    } catch (e) {
      this.publish({ error: userErrorMessage(e, '记录列表读取失败，请重试。') });
    } finally {
      this.publish({ busy: false });
    }
  }
  async open(id: string) {
    const existing = this.state.drafts.find((d) => d.id === id);
    if (existing) {
      this.select(id);
      return true;
    }
    if (this.state.busy || this.state.drafts.length >= 20) {
      this.publish({ error: '请先完成当前操作或移除不需要的本机草稿。' });
      return false;
    }
    this.publish({ busy: true, error: '' });
    try {
      const r = await this.api.get(id);
      const d: LectureDraft = {
        id: r.recordId,
        version: r.version,
        title: r.title,
        text: lectureText(r.content),
        snapshot: r.snapshot,
        revision: 0,
        savedRevision: 0,
        operation: null,
      };
      this.publish({ drafts: [d, ...this.state.drafts], activeId: id }, true);
      return true;
    } catch (e) {
      this.publish({ error: userErrorMessage(e, '记录读取失败，请重试。') });
      return false;
    } finally {
      this.publish({ busy: false });
    }
  }
  private summary(r: LectureRecord): LectureRecordSummary {
    const { content: _, snapshot, ...rest } = r;
    void _;
    return {
      ...rest,
      sourceName: snapshot.source.name,
      baseMove: snapshot.baseMove,
      moveNumber: snapshot.moveNumber,
      variationCount: snapshot.variation.length,
    };
  }
  forkActive() {
    const current = this.active();
    if (!current || current.operation || this.state.busy) return;
    if (this.state.drafts.length >= 20) {
      this.publish({ error: '请先移除不需要的本机草稿。' });
      return;
    }
    const draft: LectureDraft = {
      ...current,
      id: secureRandomUuid(),
      version: 0,
      revision: 1,
      savedRevision: -1,
      operation: null,
    };
    this.publish(
      {
        drafts: [draft, ...this.state.drafts],
        activeId: draft.id,
        error: '',
        message: '已保留为新草稿，可独立保存。',
      },
      true,
    );
  }
  async save() {
    const d = this.active();
    if (!d || this.state.busy) return;
    const body = lectureSaveBodySchema.safeParse(
      d.operation?.body ?? {
        expectedVersion: d.version,
        title: d.title,
        content: lectureContent(d.text),
        snapshot: d.snapshot,
      },
    );
    if (!body.success) {
      this.publish({ error: body.error.issues[0]?.message ?? '请检查正文。' });
      return;
    }
    const op = d.operation ?? {
      requestId: nextRequestId('lecture-save'),
      revision: d.revision,
      body: body.data,
    };
    this.publish(
      {
        busy: true,
        error: '',
        message: '正在保存…',
        drafts: this.state.drafts.map((r) => (r.id === d.id ? { ...r, operation: op } : r)),
      },
      true,
    );
    try {
      const saved = await this.api.save(d.id, op.body, op.requestId);
      const latest = this.state.drafts.find((r) => r.id === d.id);
      this.publish(
        {
          drafts: this.state.drafts.map((r) =>
            r.id === d.id
              ? { ...r, version: saved.version, savedRevision: op.revision, operation: null }
              : r,
          ),
          records: [
            this.summary(saved),
            ...this.state.records.filter((r) => r.recordId !== saved.recordId),
          ],
          message:
            latest?.revision === op.revision
              ? '已保存到服务器'
              : '上一版已保存；新输入的内容仍在草稿中，请再次保存。',
        },
        true,
      );
    } catch (e) {
      const code = typeof e === 'object' && e !== null && 'code' in e ? e.code : null;
      const definite =
        code === 'INVALID_PAYLOAD' ||
        code === 'RESOURCE_VERSION_CONFLICT' ||
        code === 'FORBIDDEN' ||
        code === 'REQUEST_ID_REUSE';
      this.publish(
        {
          drafts: this.state.drafts.map((r) =>
            r.id === d.id && definite ? { ...r, operation: null } : r,
          ),
          message: '',
          error: userErrorMessage(e, '保存结果尚未确认，请重试；正文已保留。'),
        },
        true,
      );
    } finally {
      this.publish({ busy: false });
    }
  }
}
